import { queryOptions } from "@tanstack/react-query";
import type { ApiListRequest } from "../../../../../@types/services/Api";
import type {
  ServiceQuotationFilter,
  ServiceQuotationOrder,
} from "../../../../../@types/services/ServiceQuotation";
import { useListPlantsQueryOptions } from "../../../../../__generated__/orval/endpoints/plants/plants";
import { listServiceQuotations } from "../../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type { EntitiesPlantListResponse } from "../../../../../__generated__/orval/models/entitiesPlantListResponse.zod";
import type { ListServiceQuotationsParams } from "../../../../../__generated__/orval/models/listServiceQuotationsParams.zod";
import { fetchPurchaseGroups } from "../../../../../services/purchaseGroup";
import { listStatuses } from "../../../../../services/statuses";
import { fetchUsers } from "../../../../../services/users";

type ListServiceQuotationsParamsWithSingleDate = ListServiceQuotationsParams & {
  "order[assigned_to]"?: "asc" | "desc";
  created_at?: string;
  "order[plant]"?: "asc" | "desc";
  "order[pr_item.requisition_date]"?: "asc" | "desc";
  "order[proposals_count]"?: "asc" | "desc";
  "order[purchase_group]"?: "asc" | "desc";
  "order[renegotiation]"?: "asc" | "desc";
  "order[requester]"?: "asc" | "desc";
  "order[short_description]"?: "asc" | "desc";
  "order[status]"?: "asc" | "desc";
  renegotiation?: boolean;
};

export const serviceQuotationsListQueryOptions = (
  params: ApiListRequest<ServiceQuotationFilter, ServiceQuotationOrder>,
) =>
  queryOptions({
    queryFn: () => {
      const requestParams: ListServiceQuotationsParamsWithSingleDate = {
        assigned_to: params.filters?.assigned_to?.join(","),
        created_at: params.filters?.created_at,
        identifier: params.filters?.identifier?.toString(),
        items_per_page: params.items_per_page,
        "order[assigned_to]": params.orders?.assigned_to,
        "order[created_at]": params.orders?.created_at,
        "order[identifier]": params.orders?.identifier,
        "order[plant]": params.orders?.plant,
        "order[pr_item.requisition_date]":
          params.orders?.["pr_item.requisition_date"],
        "order[proposals_count]": params.orders?.proposals_count,
        "order[purchase_group]": params.orders?.purchase_group,
        "order[renegotiation]": params.orders?.renegotiation,
        "order[requester]": params.orders?.requester,
        "order[short_description]": params.orders?.short_description,
        "order[status]": params.orders?.status,
        page: params.page,
        plant_ids: params.filters?.plant_ids,
        pr_number: params.filters?.pr_number,
        proposals_count: params.filters?.proposals_count,
        purchase_group_ids: params.filters?.purchase_group_ids,
        reference_number: params.filters?.reference_number,
        renegotiation: params.filters?.renegotiation,
        requester: params.filters?.requester?.join(","),
        short_description: params.filters?.short_description,
        status_name: params.filters?.status_name?.join(","),
      };

      return listServiceQuotations(requestParams);
    },
    queryKey: ["serviceQuotations", "list", params],
  });

export const plantListQueryOptions = (
  ids?: EntitiesPlantListResponse["results"][number]["id"][],
) =>
  useListPlantsQueryOptions(
    { id: ids },
    {
      query: {
        enabled: !!ids && ids.length > 0,
        select: (data) => data.results,
        staleTime: 1000 * 60 * 5,
      },
    },
  );

export const purchaseGroupListQueryOptions = (ids?: string[]) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () => fetchPurchaseGroups({ filters: { id: ids } }),
    queryKey: ["purchaseGroups", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5,
  });

export const userListQueryOptions = (ids?: number[]) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () =>
      fetchUsers({
        filters: {
          id: ids,
        },
      }),
    queryKey: ["users", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5,
  });

export const statusListQueryOptions = (statusNames?: string[]) =>
  queryOptions({
    enabled: !!statusNames && statusNames.length > 0,
    queryFn: () =>
      listStatuses({
        filters: {
          name: statusNames,
          scope: "service_quotations",
        },
      }),
    queryKey: ["statuses", "list", statusNames],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5,
  });
